import { useEffect, useMemo, useRef, useState } from "react";
import { inlineParts, type Attachment, type Email, type JmapClient } from "../lib/jmap";
import { sanitizeEmailHtml } from "../lib/sanitize";
import { Icon } from "./Icon";

/**
 * The body of a received message.
 *
 * HTML is preferred over the plain-text alternative — nearly all real mail is
 * multipart with the HTML part carrying the layout and the text part a degraded
 * fallback. It is sanitised and rendered in this document rather than in a
 * sandboxed frame; see lib/sanitize.ts for why, and what that costs.
 *
 * Two things make received mail actually look right:
 *
 *   - **Inline images resolve.** `cid:` references are matched to the message's
 *     own parts, downloaded through the authenticated blob endpoint, and swapped
 *     in as object URLs. Without this, signatures and embedded screenshots are
 *     broken-image icons.
 *   - **Remote images stay blocked until asked for.** They are deferred rather
 *     than stripped, so "Show images" restores them without a re-render.
 */

/** Plain text, rendered so that quoted replies and URLs read properly. */
function PlainText({ value }: { value: string }) {
  // Quote depth drives the left border, the way every other client shows it.
  const lines = value.split("\n");
  return (
    <div className="plain">
      {lines.map((line, i) => {
        const depth = /^(\s*>)+/.exec(line)?.[0].split(">").length ?? 1;
        const text = line.replace(/^(\s*>)+\s?/, "");
        return depth > 1 ? (
          <div key={i} className="quoted" style={{ paddingLeft: (depth - 1) * 10 }}>{text || " "}</div>
        ) : (
          <div key={i}>{linkify(line) || " "}</div>
        );
      })}
    </div>
  );
}

/** Turns bare URLs in plain text into links, which is what people expect. */
function linkify(line: string) {
  const parts = line.split(/(https?:\/\/[^\s<>()]+|mailto:[^\s<>()]+)/g);
  if (parts.length === 1) return line;
  return parts.map((part, i) =>
    /^(https?:\/\/|mailto:)/.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer nofollow">{part}</a>
    ) : (
      part
    ),
  );
}

export function MessageBody({
  client, email, html, text, preview, alwaysAllowRemote = false,
}: {
  client: JmapClient;
  email: Email;
  html: string | null;
  text: string | null;
  preview: string;
  alwaysAllowRemote?: boolean;
}) {
  const [showRemote, setShowRemote] = useState(alwaysAllowRemote);
  const host = useRef<HTMLDivElement>(null);

  const sanitized = useMemo(() => (html ? sanitizeEmailHtml(html) : null), [html]);

  /**
   * Resolves `cid:` images against the message's own parts.
   *
   * Runs against the rendered DOM rather than the HTML string because the
   * lookup is per-element and the object URLs must be revoked when the message
   * closes — holding a screenshot per opened message would leak for the life of
   * the tab.
   */
  useEffect(() => {
    const node = host.current;
    if (!node || !sanitized) return;

    const parts = inlineParts(email);
    if (parts.length === 0) return;

    const urls: string[] = [];
    let cancelled = false;

    const byCid = new Map<string, Attachment>();
    for (const part of parts) {
      if (part.cid) byCid.set(part.cid.replace(/^<|>$/g, "").toLowerCase(), part);
    }

    (async () => {
      const images = [...node.querySelectorAll<HTMLImageElement>('img[src^="cid:"]')];
      await Promise.all(images.map(async (img) => {
        const cid = decodeURIComponent(img.getAttribute("src")!.slice(4)).replace(/^<|>$/g, "").toLowerCase();
        const part = byCid.get(cid);
        if (!part) {
          // Referenced but absent — leave nothing rather than a broken icon.
          img.remove();
          return;
        }
        try {
          const blob = await client.download(part);
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          urls.push(url);
          img.src = url;
        } catch {
          img.remove();
        }
      }));
    })();

    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [client, email, sanitized]);

  /** Puts deferred remote images back, once the reader has asked. */
  useEffect(() => {
    const node = host.current;
    if (!node || !showRemote) return;
    node.querySelectorAll<HTMLImageElement>("img[data-remote-src]").forEach((img) => {
      img.src = img.dataset.remoteSrc!;
      img.removeAttribute("data-remote-src");
    });
  }, [showRemote, sanitized]);

  if (sanitized) {
    return (
      <>
        {sanitized.hasRemoteImages && !showRemote && (
          <div className="imgbar">
            <Icon name="shield-alert" />
            <span>Images from the sender are blocked. Loading them tells them you opened this.</span>
            <button className="pill" onClick={() => setShowRemote(true)}>Show images</button>
          </div>
        )}
        <div
          ref={host}
          className="mail-html"
          // Sanitised by DOMPurify with a mail-specific allowlist; see
          // lib/sanitize.ts. This is the one place in the app that renders
          // markup it did not author, and it does not do it casually.
          dangerouslySetInnerHTML={{ __html: sanitized.html }}
        />
      </>
    );
  }

  return <PlainText value={text ?? preview} />;
}
